import type {MouseEvent} from "react";
import React, {useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState} from "react";
import styled from "styled-components";
import type {TransformObj} from "../../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {ArrayUtils} from "../../../../../utils/data/array/ArrayUtils";
import {AppUtils} from "../../../../../utils/AppUtils";
import {ReactUtils} from "../../../../utils/ReactUtils";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {DomPortal} from "../../../../modules/abstract/portal/DomPortal";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import {useAppStore} from "../../../../../StateManager";
import {useClickOutside, useCloseOnKeyboardPress} from "../../../utils";
import type {IMultiSelectOption} from "../../../../../data/models/field/datatypes/MultiSelect";
import RemoveIcon from "../../../icons/xmark.svg?react";
import {PillStyled} from "../../../input/select/SelectInputV5";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {ColorUtils} from "../../../../../utils/ColorUtils";
import {colorPalette} from "../../../styles/colorPalette";
import {stringifySelectOption} from "../../../input/select/SelectInputUtils";
import {Functions} from "../../../../../utils/function/Functions";
import {HTMLUtils} from "../../../../../utils/HTML/HTMLUtils";
import {ObjectUtils} from "../../../../../utils/data/ObjectUtils";
import {CheckboxInputV5} from "../CheckboxInputV5";
import {
	BelowItemsStyled,
	ClearAllButtonStyled,
	DropdownStyled,
	ListItemOuterStyled,
	ListItemStyled,
	ListStyled,
	MultiSelectInputStyled,
	MultiSelectOptionStyled,
} from "./MultiSelect.styled";
import {MoreButtonStyled} from "./MultiLineInputV5";

interface IMultiSelectInputProps<T> {
	readonly listItemStyle?: React.CSSProperties;
	readonly options: T[];
	readonly selected?: T[];
	readonly disabled?: boolean;
	readonly runOnClickEvenWhenDisabled?: boolean;
	readonly searchBar?: boolean;
	readonly noFixedPosition?: boolean;
	readonly focused?: boolean;
	readonly inline?: boolean;
	readonly isSameWidth?: boolean;
	readonly dropdownStyle?: React.CSSProperties;
	readonly isInherited?: boolean;
	readonly verticalAlignment?: VerticalAlignment;
	readonly pillClassName?: string;
	readonly renderOptionWhenClosed?: (item: T) => React.ReactNode;
	readonly renderOptionWhenOpen?: (item: T) => React.ReactNode; // if not provided, it uses renderOptionWhenClosed
	readonly stringifyOption?: (item: T) => string;
	readonly onChange?: (selectedOptions: T[]) => void;
	readonly onClick?: (e: React.MouseEvent) => void;
}

enum MultiSelectInputState {
	Default = 1,
	Expanded = 2,
}

export const MultiSelectInputV5 = <T,>({
	listItemStyle = {},
	options = [],
	disabled = false,
	runOnClickEvenWhenDisabled = false,
	pillClassName = "",
	renderOptionWhenClosed = (option: any) => option,
	renderOptionWhenOpen = renderOptionWhenClosed,
	stringifyOption = stringifySelectOption,
	searchBar = true,
	isSameWidth = true,
	dropdownStyle = {},
	selected,
	noFixedPosition,
	focused,
	inline,
	isInherited = false,
	verticalAlignment,
	onChange,
	onClick,
}: IMultiSelectInputProps<T>) => {
	const _element = useRef<HTMLDivElement>(null);
	const _list = useRef<HTMLDivElement>(null);
	const _lockedAlignmentRef = useRef<VerticalAlignment>(null);

	const appState = useAppStore((store) => store.appState);

	const [search, setSearch] = useState<string>("");
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [multiSelectState, setMultiSelectState] = useState<MultiSelectInputState>(MultiSelectInputState.Default);
	const [transform, setTransform] = useState<TransformObj>(null);
	const [dropdownWidthPx, setDropdownWidthPx] = useState(320);
	const selectedLocally = useRef<T[]>([...(selected ?? [])]);
	const selectedFromProps = useRef<T[]>([...(selected ?? [])]);
	const previousSelectedProp = useRef(selected);
	const hasLocalChanges = useRef(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const incomingSelected = selected ?? [];
	const isClickable = !disabled || (runOnClickEvenWhenDisabled && !!onClick);

	// Callers often produce a fresh selected array on every table render. Display
	// actual changes immediately, without a second render or resetting pending edits.
	// A copied snapshot also detects in-place mutations to observable arrays.
	const selectionChanged =
		incomingSelected.length !== selectedFromProps.current.length ||
		incomingSelected.some((item, index) => item !== selectedFromProps.current[index]) ||
		(!hasLocalChanges.current &&
			selected !== previousSelectedProp.current &&
			(incomingSelected.length !== selectedLocally.current.length ||
				incomingSelected.some((item, index) => item !== selectedLocally.current[index])));
	const currentSelected = selectionChanged ? incomingSelected : selectedLocally.current;

	useLayoutEffect(() => {
		previousSelectedProp.current = selected;
		if (selectionChanged) {
			selectedFromProps.current = [...incomingSelected];
			selectedLocally.current = [...incomingSelected];
			hasLocalChanges.current = false;
		}
	});

	const dispatchChange = useCallback(
		(options: T[]) => {
			if (disabled) {
				return;
			}
			let values = options.filter((item) => item && !Array.isArray(item));
			values = ArrayUtils.uniq(values);
			if (!ObjectUtils.compare(selected, values)) {
				onChange?.(values);
			}
		},
		[onChange, selected, disabled],
	);

	const onToggleValue = (value: T, event?: React.MouseEvent) => {
		event?.stopPropagation();
		if (disabled) {
			return;
		}
		// if T is an object selected does not includes value
		const valueToRemove = selectedLocally.current.find((o) => value === o);
		let newSelected: T[] = [];

		if (valueToRemove) {
			newSelected = ArrayUtils.remove(selectedLocally.current, valueToRemove);
		} else {
			newSelected = ArrayUtils.add(selectedLocally.current, value);
		}

		selectedLocally.current = newSelected;
		hasLocalChanges.current = isEditing;
		forceUpdate();

		if (!isEditing) {
			dispatchChange(newSelected);
		}
	};

	const saveLocalChanges = useCallback(() => {
		if (hasLocalChanges.current) {
			hasLocalChanges.current = false;
			dispatchChange(selectedLocally.current);
		}
	}, [dispatchChange]);
	const latestState = useRef({saveLocalChanges, isEditing});

	useLayoutEffect(() => {
		latestState.current = {saveLocalChanges, isEditing};
	});

	useEffect(() => {
		return () => {
			latestState.current.saveLocalChanges();
			if (latestState.current.isEditing) {
				AppUtils.disableScrolling(false);
			}
		};
	}, []);

	const onClose = useCallback(() => {
		if (isEditing) {
			AppUtils.disableScrolling(false);
			setSearch("");
			setIsEditing(false);
			_lockedAlignmentRef.current = null;
			saveLocalChanges();
		}
	}, [isEditing, saveLocalChanges]);

	const onListClick = useCallback(
		(event?: React.MouseEvent) => {
			event.stopPropagation();
			if (!disabled) {
				if (isEditing) {
					if (!HTMLUtils.isDescendant(_list.current, event?.target as Element)) {
						onClose();
					}
				} else {
					onClick?.(event);
					setIsEditing(true);
					AppUtils.disableScrolling(true);
				}
			} else if (runOnClickEvenWhenDisabled) {
				onClick?.(event);
			}
		},
		[onClick, isEditing, disabled, onClose, runOnClickEvenWhenDisabled],
	);

	const filterOption = (option: T) => {
		if (search) {
			const text = stringifyOption(option).toLowerCase();

			return text.includes(search.toLowerCase());
		}

		return true;
	};

	const onRemoveAll = (e: React.MouseEvent) => {
		e.stopPropagation();
		selectedLocally.current = [];
		hasLocalChanges.current = false;
		forceUpdate();
		dispatchChange([]);
	};

	const handleListStateChange = (state: MultiSelectInputState) => (e: React.MouseEvent) => {
		e.stopPropagation();
		setMultiSelectState(state);
	};

	const expandList = handleListStateChange(MultiSelectInputState.Expanded);
	const defaultList = handleListStateChange(MultiSelectInputState.Default);

	const sortOptions = (a: T, b: T) => StringUtils.sortIgnoreCase(stringifyOption(a), stringifyOption(b));

	const renderPill = (item: T, index: number) => {
		return (
			<PillStyled
				className={`selectedItem ${pillClassName}`}
				key={`${(item as IMultiSelectOption).value ?? (item as string)}_${index}`}
				$isMultiselect={true}
				$color={(item as IMultiSelectOption).color ? `#${(item as IMultiSelectOption).color}` : ""}
			>
				<span>{renderOptionWhenClosed(item)}</span>
				{!disabled && (
					<RemoveIcon
						className="removeItem"
						onClick={(e) => onToggleValue(item, e)}
					/>
				)}
			</PillStyled>
		);
	};

	const getCurrentState = () => {
		const maxItemCount = 10;

		switch (multiSelectState) {
			case MultiSelectInputState.Default:
				return (
					<MultiSelectOptionsWrapper>
						<MultiSelectOptionStyled>
							{currentSelected.toSorted(sortOptions).map((item, index) => {
								if (index < maxItemCount) {
									return renderPill(item, index);
								}
							})}
						</MultiSelectOptionStyled>
						{currentSelected.length > maxItemCount && (
							<BelowItemsStyled onClick={expandList}>
								<MoreButtonStyled>{`+${currentSelected.length - maxItemCount} more`}</MoreButtonStyled>
							</BelowItemsStyled>
						)}
					</MultiSelectOptionsWrapper>
				);
			case MultiSelectInputState.Expanded:
				return (
					<MultiSelectOptionsWrapper>
						<MultiSelectOptionStyled>{currentSelected.toSorted(sortOptions).map((item, index) => renderPill(item, index))}</MultiSelectOptionStyled>
						{currentSelected.length > maxItemCount && (
							<BelowItemsStyled onClick={defaultList}>
								<MoreButtonStyled>Show less</MoreButtonStyled>
							</BelowItemsStyled>
						)}
					</MultiSelectOptionsWrapper>
				);
		}
	};

	useClickOutside([_element, _list], onClose, "mouseup", [], false, isEditing);
	useCloseOnKeyboardPress(onClose, KeyboardListener.KEY_ENTER, [], {enabled: isEditing});

	let filteredOptions: T[] = [];
	if (isEditing) {
		const removedSelected = currentSelected.filter((option) => !options.includes(option));
		filteredOptions = [...options, ...removedSelected].filter(filterOption);
	}

	useLayoutEffect(() => {
		if (!isEditing) {
			return;
		}

		const anchorWidth = isSameWidth ? (_element.current?.offsetWidth ?? 320) : 320;
		setDropdownWidthPx(Math.min(600, Math.max(320, anchorWidth)));
	}, [isEditing, isSameWidth]);

	useLayoutEffect(() => {
		if (_element.current && _list.current && isEditing) {
			if (!_lockedAlignmentRef.current) {
				const listHeight = _list.current.offsetHeight;
				const parentSize = _element.current.getBoundingClientRect();
				const spaceBelow = window.innerHeight - parentSize.bottom;
				_lockedAlignmentRef.current = spaceBelow >= listHeight ? VerticalAlignment.bottomOuter : VerticalAlignment.topOuter;
			}

			setTransform(
				DomUtils.getFixedFloatingElementPosition(
					_element.current,
					_list.current,
					verticalAlignment ?? _lockedAlignmentRef.current,
					HorizontalAlignment.center,
					0,
					0,
					false,
				),
			);
		}
	}, [isEditing, verticalAlignment, filteredOptions.length, currentSelected.length, dropdownWidthPx]);

	useEffect(() => {
		if (!disabled) {
			_list.current?.click();
		}
	}, [focused, disabled]);

	return (
		!(disabled && !runOnClickEvenWhenDisabled && !onClick && currentSelected.length === 0) && (
			<MultiSelectInputStyled
				onClick={onListClick}
				ref={_element}
				className={ReactUtils.cls({
					inline,
					empty: currentSelected.length === 0,
					cellContent: inline,
					isActive: isEditing,
					isInherited,
					disabled: !isClickable,
				})}
				$emptyInline={inline && currentSelected.length === 0}
			>
				{getCurrentState()}
				{isEditing && (
					<DomPortal destination={appState.app.modalContainer}>
						<DropdownStyled
							className={ReactUtils.cls({inline})}
							ref={_list}
							style={{
								transform: noFixedPosition ? "" : transform?.translate,
								visibility: transform?.translate ? "visible" : "hidden",
								...dropdownStyle,
								width: `${dropdownWidthPx}px`,
								minWidth: `${dropdownWidthPx}px`,
								maxWidth: "600px",
							}}
							$noFixedPosition={noFixedPosition}
						>
							{options.length > 5 && searchBar && (
								<SearchFieldV5
									value={search}
									onInput={(value) => setSearch(value)}
									placeholder="Find"
									autoFocus={true}
									onClick={Functions.stopPropagation}
								/>
							)}
							<ListStyled>
								{filteredOptions.map((option, index) => {
									const label = renderOptionWhenOpen(option);
									const isSelected = currentSelected.includes(option);
									const optionText = stringifyOption(option);
									const backgroundColor = (option as IMultiSelectOption).color ? `#${(option as IMultiSelectOption).color}` : "transparent";
									const hslColor = ColorUtils.hex2hsl(backgroundColor);
									const isLightColor = hslColor.l >= 0.83 || !backgroundColor || backgroundColor === "transparent";
									const fontColor =
										backgroundColor === "transparent"
											? isSelected
												? colorPalette.primary.c500Primary
												: ""
											: isLightColor
												? colorPalette.black
												: colorPalette.white;

									return (
										<ListItemOuterStyled
											key={`${(option as IMultiSelectOption).value ?? (option as string)}_${index}`}
											className={ReactUtils.cls({selected: isSelected})}
										>
											<CheckboxInputV5
												value={isSelected}
												disabled={disabled}
												onChange={() => onToggleValue(option)}
												className="checkbox"
											/>
											<ListItemStyled
												onClick={(event: MouseEvent) => onToggleValue(option, event)}
												style={listItemStyle}
												className={ReactUtils.cls({removed: !options.includes(option), selected: isSelected})}
												$backgroundColor={backgroundColor}
												$fontColor={fontColor}
												title={typeof label === "string" ? (label as string) : optionText}
											>
												<span className="label">{label}</span>
											</ListItemStyled>
										</ListItemOuterStyled>
									);
								})}
							</ListStyled>
							{filteredOptions.length === 0 && (
								<div className="bottom-section expanded noValues">
									No values found.
									<br />
									Contact your admin.
								</div>
							)}
							<ClearAllButtonStyled
								onClick={!disabled && currentSelected.length > 0 && filteredOptions.length > 0 ? onRemoveAll : undefined}
								title="Clear All"
								className={ReactUtils.cls({disabled: disabled || currentSelected.length === 0 || filteredOptions.length === 0})}
							>
								Clear All
							</ClearAllButtonStyled>
						</DropdownStyled>
					</DomPortal>
				)}
			</MultiSelectInputStyled>
		)
	);
};

export const MultiSelectOptionsWrapper = styled.div`
	flex: 1;
	min-width: 0;
`;
